/**
 * Standardized API error handling utilities.
 *
 * Usage in catch blocks:
 *   toast.error(getApiErrorMessage(err))
 *
 * For 404 redirects in client components, check status first:
 *   if (err instanceof ApiError && err.status === 404) router.push('/not-found')
 *   else toast.error(getApiErrorMessage(err))
 */

import { ApiError } from './api'

/**
 * Map an ApiError (or unknown throw) to a user-readable string.
 *
 * Status-code conventions:
 *  400  → join details[].message if present; otherwise use err.message
 *  403  → "You don't have permission to do this."
 *  404  → "The requested resource was not found." (callers may prefer to redirect)
 *  429  → rate-limit guidance
 *  5xx  → generic server error
 *  other → err.message or generic fallback
 */
export function getApiErrorMessage(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return 'Something went wrong. Please try again.'
  }

  switch (true) {
    case err.status === 400:
      if (err.details && err.details.length > 0) {
        return err.details.map(d => d.message).join(' · ')
      }
      return err.message || 'Invalid request. Please check your input.'

    case err.status === 403:
      return "You don't have permission to do this."

    case err.status === 404:
      return 'The requested resource was not found.'

    case err.status === 429:
      return 'Too many requests. Please wait a moment and try again.'

    case err.status >= 500:
      return 'Something went wrong on our end. Please try again later.'

    default:
      return err.message || 'Something went wrong.'
  }
}
